import { useNavigate } from "react-router-dom";
import AppBar from "../../atoms/appbar";
import Button from "../../atoms/button";
import Input from "../../atoms/input";
import { useForm } from "react-hook-form";
import * as React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import type { LoginInput } from "../../../types/auth.types";
import { loginSchema } from "../../../lib/schemas/auth.schema";
import { useMutation } from "@tanstack/react-query";
import type { OTPRequest } from "../../../@apis/dto/request/auth.request";
import {
  getMyInfo,
  loginConfirmOtp,
  requestOtp,
} from "../../../@apis/v1/auth.service";
import { useAccessStore } from "../../../lib/store/useAccessStore";
import { flushSync } from "react-dom";
import {
  alertRequestFailure,
  apiErrorMessage,
  apiErrorOf,
  isTimeoutError,
  serverErrorMessage,
  setGlobalAuthHeader,
} from "../../../@apis/axios.config";
import { useModalStore } from "../../../lib/store/useModalStore";
import { resetSessionQueries, seedMyInfoQuery } from "../../../@apis/queryClient";
import { useMyInfoStore } from "../../../lib/store/useMyInfoStore";
import dayjs from "dayjs";
import { requestPushTokenRegistration } from "../../../lib/utils/boot";
import { useNativeOtpAutofill } from "../../../hooks/useNativeOtpAutofill";
import { cn } from "../../../lib/utils/cn";

interface Props {
  prev: () => void;
}

export default function LoginScreen({ prev }: Props) {
  const { openModal } = useModalStore();
  const navigate = useNavigate();
  const [request, setRequest] = React.useState(false);
  const [rejectedOtp, setRejectedOtp] = React.useState<string | null>(null);
  const { setAccess } = useAccessStore();
  const { setMyInfo } = useMyInfoStore();

  const {
    register,
    handleSubmit,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      otp: "",
      mobileno: "",
    },
  });
  const values = watch();
  const otpFilled = values.otp.length === 6;
  const { startListen } = useNativeOtpAutofill((otp) => {
    setValue("otp", otp, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  });

  React.useEffect(() => {
    startListen();
  }, [startListen]);

  // 요청마다 자동 입력 수신 시간을 새로 잡는다
  const handleRequestOtp = () => {
    const mobileno = getValues("mobileno");
    if (mobileno.length < 10) return;
    const sentOtp = getValues("otp");
    startListen();
    loginRequestOTPMuation.mutate(
      { mobileno },
      {
        // 응답 전 자동 입력된 새 번호는 유지
        onSuccess: (res) => {
          if (res.success && getValues("otp") === sentOtp) setValue("otp", "");
        },
      },
    );
  };

  const onSubmit = (form: LoginInput) => {
    confirmOTPMutation.mutate(form);
  };

  const onError = () => {
    const firstError = Object.values(errors)[0];

    if (firstError && firstError.message) {
      openModal("ALERT_DIALOG", {
        title: "에러",
        content: firstError.message.toString(),
      });
    }
  };

  const loginRequestOTPMuation = useMutation({
    mutationFn: (form: OTPRequest) => requestOtp(form),
    onSuccess: (res) => {
      const { success } = res;
      if (success) {
        return setRequest(true);
      }
    },
    onError: (err) => {
      if (isTimeoutError(err)) return;
      openModal("ALERT_DIALOG", {
        title: request ? "인증번호 재발송 실패" : "인증번호 발송 실패",
        content:
          apiErrorOf(err)?.code === "P018"
            ? "이미 인증번호를 발송하셨습니다.\n3분 후에 다시 시도해 주세요."
            : serverErrorMessage(err),
      });
    },
  });

  const confirmOTPMutation = useMutation({
    mutationFn: (form: LoginInput) => loginConfirmOtp(form),
    onSuccess: async (res) => {
      const { success, data, error } = res;
      if (success) {
        flushSync(() => {
          setGlobalAuthHeader(data.accessToken);
          setAccess({ ...data, expiresAt: dayjs().add(10, "minutes").unix() });
        });
        resetSessionQueries();
        try {
          requestPushTokenRegistration({ force: true });
          const myInfoRes = await getMyInfo();
          if (myInfoRes.success) {
            setMyInfo(myInfoRes.data);
            seedMyInfoQuery(myInfoRes);
          }
        } catch {
          // 푸시 등록·내 정보는 이후 재시도된다
        }
        navigate("/", { replace: true });
      } else {
        openModal("ALERT_DIALOG", {
          title: "에러",
          content: apiErrorMessage(error),
        });
      }
    },
    onError: (err, form) => {
      if (apiErrorOf(err)?.code === "P017") setRejectedOtp(form.otp);
      alertRequestFailure("에러", err);
    },
  });

  const requesting = loginRequestOTPMuation.isPending;
  const confirming = confirmOTPMutation.isPending;
  const canRequest = values.mobileno.length >= 10 && !requesting;
  // 틀린 번호로 확인되면 재요청 허용
  const resendLocked =
    requesting || confirming || (otpFilled && values.otp !== rejectedOtp);
  const loginDisabled = !otpFilled || confirming;

  return (
    <div>
      <AppBar title="로그인" backButton onBack={prev} />
      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <section
          className={cn(
            "flex flex-col w-full px-[15px] pb-[15px]",
            request ? "gap-[10px]" : "justify-between min-h-[calc(100vh-56px)]",
          )}
        >
          <div className="space-y-[10px]">
            <p className="font-bold pb-[10px] text-base">휴대전화번호</p>
            <Input
              placeholder="전화번호 - 없이 입력"
              type="tel"
              inputMode="numeric"
              maxLength={11}
              readOnly={request}
              className={
                request
                  ? "bg-[#E8EAF4] border-transparent text-sm font-bold text-[#222] focus:ring-0 focus:border-transparent"
                  : "text-sm placeholder:text-sm"
              }
              {...register("mobileno", {
                onChange: (e) => {
                  e.target.value = e.target.value
                    .replace(/[^0-9]/g, "")
                    .slice(0, 11);
                },
              })}
            />
            {request ? (
              <div className="flex gap-[8px] items-stretch">
                <div className="min-w-0 flex-1">
                  <Input
                    maxLength={6}
                    inputMode="numeric"
                    className="min-w-0 text-sm placeholder:text-sm"
                    placeholder="인증번호 6자리 입력"
                    {...register("otp")}
                  />
                </div>
                <Button
                  type="button"
                  className="shrink-0 whitespace-nowrap px-[16px]"
                  disabled={resendLocked}
                  variant={resendLocked ? "disabled" : "black"}
                  label="재요청"
                  onClick={handleRequestOtp}
                />
              </div>
            ) : (
              <Button
                type="button"
                className="w-full text-white"
                disabled={!canRequest}
                variant={canRequest ? "black" : "gray"}
                label="인증 요청"
                onClick={handleRequestOtp}
              />
            )}
          </div>

          <div className={request ? "mt-[14px]" : ""}>
            <Button
              type="submit"
              disabled={loginDisabled}
              className="w-full text-sm"
              variant={loginDisabled ? "disabled" : "primary"}
              label="로그인"
            />
          </div>
        </section>
      </form>
    </div>
  );
}
